import { PencilIcon } from 'components/icons';
import { FormattedDate, useFormatDate } from 'components/misc/FormattedDate';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Ref } from 'react';
import { FormattedMessage } from 'react-intl';
import { getClassifierText, t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { InvoiceDetailsParameterizedTracker } from '../InvoiceDetailsChangeTrackers';
import { ITEM_EDIT_CELL, ITEM_FIGURE, ITEM_SELECT_CELL } from '../InvoiceItemTable';
import { itemTableRowClassName } from '../InvoiceItemRow';
import { AdvancedBillingDisplay } from './component/AdvancedBillingDisplay';
import { DescriptionDisplay } from './component/DescriptionDisplay';
import { OriginalAmountDisplay } from './component/OriginalAmountDisplay';
import { TakeActionBackDisplay } from './component/TakeActionBackDisplay';
import { TotalAmountDisplay } from './component/TotalAmountDisplay';
import { InvoiceExpenseRowProps, useInvoiceExpenseRow } from './InvoiceExpenseTableItem';

/**
 * An expense as a row of the new design's Expenses table: its content is the classic row's, its cells the table's
 * columns. In a narrow card the figures fold under the entry and their cells go.
 */
export function InvoiceExpenseTableRow({
    measureRef,
    virtualIndex,
    isNarrow,
    ...props
}: InvoiceExpenseRowProps & { measureRef: Ref<HTMLTableRowElement>; virtualIndex: number; isNarrow: boolean }) {
    const {
        invoiceExpense,
        expenseForm,
        isNew,
        index,
        readonly,
        invoice,
        classifiers,
        control,
        setValue,
        getValues,
        handleUpdateInvoiceExpenses,
        advancedBillingEnabled,
    } = props;
    const {
        isCredit,
        isPaymentCurrencyMode,
        displayCurrency,
        areButtonsDisabled,
        editIntentTypeCode,
        invoiceExpenseDate,
        invoiceExpenseEmployee,
        isNonBillable,
        onSubmit,
        openInvoiceExpenseEditModal,
        isRowDisabled,
        canTakeActionBack,
        hideSelectionCheckbox,
        rowState,
    } = useInvoiceExpenseRow(props);
    const formatName = useEmployeeNameFormatter();
    const formatDate = useFormatDate();
    const { employee, expense } = invoiceExpense;
    const expenseTypeCode = invoiceExpense.expenseTypeCode || expense?.expenseTypeCode;
    // What names this row in its controls' labels: its date and who added it.
    const rowName = { date: formatDate(invoiceExpenseDate) ?? '', name: employee ? formatName(employee) : '' };
    const submit = () => {
        void onSubmit();
    };

    const originalAmount = (
        <OriginalAmountDisplay
            invoiceExpense={invoiceExpense}
            readonly={readonly}
            isRowDisabled={isRowDisabled}
            control={control}
            index={index}
            onSubmit={submit}
            setValue={setValue}
            advancedBillingEnabled={advancedBillingEnabled}
            isPaymentCurrencyMode={isPaymentCurrencyMode}
            displayCurrency={displayCurrency}
            invoice={invoice}
        />
    );
    const amount = (
        <TotalAmountDisplay
            invoiceExpense={invoiceExpense}
            currency={displayCurrency}
            isPaymentCurrencyMode={isPaymentCurrencyMode}
        />
    );
    const advancedBilling = (
        <AdvancedBillingDisplay
            readonly={readonly}
            isRowDisabled={isRowDisabled}
            control={control}
            index={index}
            onSubmit={submit}
            setValue={setValue}
            getValues={getValues}
        />
    );

    return (
        <tr ref={measureRef} data-index={virtualIndex} className={itemTableRowClassName(rowState)}>
            <td className={ITEM_SELECT_CELL}>
                {hideSelectionCheckbox ? null : (
                    <Checkbox
                        className="mt-0.5"
                        aria-label={t('invoices.details.table.select-expense', rowName)}
                        disabled={areButtonsDisabled}
                        checked={!!expenseForm?.selected}
                        onCheckedChange={(checked) =>
                            setValue(`invoiceExpenses.${index}.selected`, checked, { shouldDirty: true })
                        }
                    />
                )}
            </td>
            <td className="px-2 py-3">
                {isRowDisabled && canTakeActionBack ? (
                    <div className="mb-2">
                        <TakeActionBackDisplay
                            index={index}
                            invoiceExpense={invoiceExpense}
                            editIntentTypeCode={editIntentTypeCode}
                            setValue={setValue}
                            control={control}
                            handleUpdateInvoiceExpenses={handleUpdateInvoiceExpenses}
                        />
                    </div>
                ) : null}
                <div className="opacity-decreasing-when-disabled">
                    <div className="text-meta flex flex-wrap items-center gap-x-2.5 gap-y-1">
                        {employee ? <EmployeeNameLink entity={employee} to={`/employee/${employee.id}`} /> : null}
                        <InvoiceDetailsParameterizedTracker
                            currentParam={invoiceExpenseEmployee?.id}
                            displayValue={expense?.employee ? formatName(expense.employee) : undefined}
                            storedParam={expense?.employee?.id}
                            textWrap="nowrap"
                        />
                        <span className="text-foreground-secondary tabular-nums">
                            <FormattedDate value={invoiceExpenseDate} fallback="" />
                        </span>
                        {expenseTypeCode ? (
                            <span className="text-foreground-tertiary">
                                {getClassifierText(classifiers['EXPENSE_TYPE'], expenseTypeCode)}
                            </span>
                        ) : null}
                        {isNonBillable ? (
                            <Badge className="bg-neutral-tint text-foreground-secondary">
                                <FormattedMessage id="expenses.form.is-non-billable" />
                            </Badge>
                        ) : null}
                        {isNew ? (
                            <Badge className="bg-card text-success-ink font-semibold">
                                <FormattedMessage id="invoices.details.table.new-row" />
                            </Badge>
                        ) : null}
                    </div>
                    <DescriptionDisplay
                        invoiceExpense={invoiceExpense}
                        readonly={readonly}
                        isRowDisabled={isRowDisabled}
                        control={control}
                        index={index}
                        onSubmit={submit}
                        editLabel={t('invoices.details.table.edit-description', rowName)}
                    />
                </div>
                {isNarrow ? (
                    <div className="opacity-decreasing-when-disabled mt-1.5 flex flex-col gap-2">
                        <div className="text-meta flex items-start justify-between gap-3">
                            {originalAmount}
                            <span className="text-body1 font-semibold">{amount}</span>
                        </div>
                        {advancedBillingEnabled ? advancedBilling : null}
                    </div>
                ) : null}
            </td>
            {isNarrow ? null : (
                <td className="w-40 px-2 py-3">
                    <div className="opacity-decreasing-when-disabled text-meta">{originalAmount}</div>
                </td>
            )}
            {!isNarrow && advancedBillingEnabled ? <td className="w-60 px-2 py-3">{advancedBilling}</td> : null}
            {isNarrow ? null : (
                <td className="w-32 px-2 py-3 text-end">
                    <div className={`${ITEM_FIGURE} flex justify-end`}>{amount}</div>
                </td>
            )}
            <td className={ITEM_EDIT_CELL}>
                {isCredit || readonly || isRowDisabled ? null : (
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('invoices.details.table.edit-expense', rowName)}
                        disabled={areButtonsDisabled}
                        onClick={openInvoiceExpenseEditModal}
                        className="text-foreground-tertiary new:[&_svg[stroke]]:stroke-[1.5]"
                    >
                        <PencilIcon className="size-[15px]" />
                    </Button>
                )}
            </td>
        </tr>
    );
}
